import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import FileUpload from "../../common/FileUpload";
import ServiceCategorySelector from "../../ServiceCategory/ServiceCategorySelector";
import { useProfile } from "../../../context/ProfileContext";
import { httpService } from "../../../services/httpService";
import { FiXCircle, FiCheck } from "react-icons/fi";
 
// Memoize the FormField component
const FormField = React.memo(
  ({
    label,
    required,
    error,
    children,
    labelCheckBoxNode = "",
    classApplied = "",
  }) => (
    <div className={`space-y-2 ${classApplied}`}>
      <label className="block">
        {label} {required && <span className="text-red-500">*</span>}
        {labelCheckBoxNode}
      </label>
      {children}
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  )
);
 
FormField.displayName = "FormField";
 
const RoomForm = ({
  isActive,
  roomId: propRoomId,
  // setServiceCategory,
  data,
  updateData,
  onValidationChange,
  onAddRoom,
  roomListVersion,
}) => {
  const { rawServiceCategory } = useProfile();
  const isActiveFlag = (st) =>
    st && (st.is_active === 1 || st.is_active === "1" || st.is_active === true);

  const [categories] = useState(
    rawServiceCategory
      .filter(
        (cat) => cat.type_id === 2 && cat.service_category_name === "Boarding",
      )
      .map((cat) => ({
        ...cat,
        service_type: Array.isArray(cat.service_type)
          ? cat.service_type.filter(
              (st) => isActiveFlag(st) && st.service_type_name !== "Pet Resort",
            )
          : [],
      }))
      .filter((cat) => cat.service_type.length > 0),
  );

  // Pet Types State - Multi Select
  const [petTypes, setPetTypes] = useState([]);
  const [selectedPetTypes, setSelectedPetTypes] = useState([]); // Full objects
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectAll, setSelectAll] = useState(false);
  const [loadingPetTypes, setLoadingPetTypes] = useState(false);

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]); // full room objects
  const [roomsDropdownOpen, setRoomsDropdownOpen] = useState(false);
  const roomsDropdownRef = useRef(null);
  const [form, setForm] = useState({
    ...data,
    taxable: data.tax_percentage > 0 ? "yes" : "no",
    taxPercentage: data.tax_percentage?.toString() || "",
    pet_type: data.pet_type || [], // Now an array of IDs
    roomTypeSelections: data.roomTypeSelections || {},
    selectedRoomTypes: data.selectedRoomTypes || [],
    roomTypeOptions: data.roomTypeOptions || {},
  });

  const [serviceNameExists, setServiceNameExists] = useState(false);
  const [checkingServiceName, setCheckingServiceName] = useState(false);

  // Fetch Pet Types from API
  useEffect(() => {
    const fetchPetTypes = async () => {
      setLoadingPetTypes(true);
      try {
        const response = await httpService.get(
          "/vendors/getPetTypes?isEnabled=true",
        );
        const list = response.data?.data ?? response.data ?? [];

        // const formattedTypes = list
        //   .filter((pt) => pt.enabled)
        //   .map((pt) => ({
        //     _id: pt._id,
        //     pet_type_id: pt.pet_type_id || pt._id,
        //     name: pt.name || pt.pet_type_name || "Unknown Pet Type",
        //   }));
        const formattedTypes = list.map((pt) => ({
          _id: pt._id,
          pet_type_id: Array.isArray(pt.pet_type_id)
            ? pt.pet_type_id[0]
            : pt.pet_type_id || pt._id,
          name: pt.name || pt.pet_type_name || "Unknown Pet Type",
        }));

        setPetTypes(formattedTypes.length > 0 ? formattedTypes : []);
      } catch (error) {
        console.error("Error fetching pet types:", error);
        toast.error("Failed to load pet types.");
        setPetTypes([]);
      } finally {
        setLoadingPetTypes(false);
      }
    };

    if (isActive) {
      fetchPetTypes();
    }
  }, [isActive]);

  useEffect(() => {
    const fetchRooms = async () => {
      setLoadingRooms(true);
      try {
        const response = await httpService.get("/room/getRooms");
        const list = response?.data?.data ?? response?.data ?? [];
        setRooms(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Error fetching rooms:", error);
        toast.error("Failed to load rooms.");
        setRooms([]);
      } finally {
        setLoadingRooms(false);
      }
    };

    if (isActive) {
      fetchRooms();
    }
  }, [isActive, roomListVersion]);

  // Sync form with incoming data
  useEffect(() => {
    if (isActive) {
      setForm((prev) => ({
        ...prev,
        ...data,
        category: propRoomId ? data.category : prev.category,
        taxable:
          data.taxable !== undefined
            ? data.taxable
            : data.tax_percentage > 0
              ? "yes"
              : "no",
        taxPercentage:
          data.taxPercentage !== undefined
            ? data.taxPercentage
            : data.tax_percentage?.toString() || "",
        pet_type: data.pet_type || [],
        roomTypeSelections:
          data.roomTypeSelections || prev.roomTypeSelections || {},
        selectedRoomTypes:
          data.selectedRoomTypes || prev.selectedRoomTypes || [],
        roomTypeOptions: data.roomTypeOptions || prev.roomTypeOptions || {},
      }));
    }
  }, [data, isActive, propRoomId]);

  // Track initialization status
  const isPetTypeInitialized = useRef(false);

  // Close rooms dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        roomsDropdownRef.current &&
        !roomsDropdownRef.current.contains(event.target)
      ) {
        setRoomsDropdownOpen(false);
      }
    };
    if (roomsDropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () =>
        document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [roomsDropdownOpen]);

  useEffect(() => {
    if (isActive && petTypes.length > 0) {
      if (data.pet_type && data.pet_type.length > 0) {
        const preSelected = petTypes.filter((pt) =>
          (data.pet_type || []).some(
            (pet) =>
              // Check for both formats
              pet.pet_type_id === pt.pet_type_id ||
              pet.id === pt.pet_type_id ||
              (typeof pet === "string" && pet === pt.pet_type_id),
          ),
        );
        setSelectedPetTypes(preSelected);
      }
      isPetTypeInitialized.current = true;
    }
  }, [isActive, petTypes, data.pet_type]);

  // Sync selectedPetTypes → form.pet_type (array of IDs)
  useEffect(() => {
    if (!isPetTypeInitialized.current && selectedPetTypes.length === 0) return;
    const petTypeIds = selectedPetTypes.map((pt) => pt.pet_type_id || pt._id);
    const prevIds = form.pet_type || [];
    if (JSON.stringify(prevIds) === JSON.stringify(petTypeIds)) return;

    setForm((prev) => ({ ...prev, pet_type: petTypeIds }));
    updateData?.({ ...form, pet_type: petTypeIds });
  }, [selectedPetTypes, updateData]);

  // Update selectAll state
  useEffect(() => {
    if (petTypes.length > 0) {
      setSelectAll(selectedPetTypes.length === petTypes.length);
    }
  }, [selectedPetTypes, petTypes]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () =>
        document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [dropdownOpen]);

  const filteredRooms = useMemo(() => {
    if (!selectedPetTypes || selectedPetTypes.length === 0) return [];
    const selectedIds = selectedPetTypes.map((pt) =>
      String(pt.pet_type_id || pt._id),
    );
    return rooms.filter((room) =>
      (room.pet_type || []).some((pt) =>
        selectedIds.includes(String(pt.id || pt._id || pt.pet_type_id)),
      ),
    );
  }, [rooms, selectedPetTypes]);

  const toggleRoom = (room) => {
    const roomId = String(room._id || room.id);
    const exists = selectedRooms.some(
      (selectedRoom) => String(selectedRoom._id || selectedRoom.id) === roomId,
    );
    const nextRooms = exists
      ? selectedRooms.filter(
          (selectedRoom) =>
            String(selectedRoom._id || selectedRoom.id) !== roomId,
        )
      : [...selectedRooms, room];
    const roomIds = nextRooms.map((selectedRoom) =>
      selectedRoom._id || selectedRoom.id,
    );
    const nextForm = {
      ...form,
      selectedRoomTypes: roomIds,
      roomTypeOptions: roomIds.reduce(
        (options, id) => ({
          ...options,
          [id]: form.roomTypeOptions?.[id] ?? "0",
        }),
        {},
      ),
    };
    setSelectedRooms(nextRooms);
    setForm(nextForm);
    updateData?.(nextForm);
  };

  const removeRoom = (roomId) => {
    const nextRooms = selectedRooms.filter(
      (room) => String(room._id || room.id) !== String(roomId),
    );
    const nextIds = nextRooms.map((room) => room._id || room.id);
    const nextForm = {
      ...form,
      selectedRoomTypes: nextIds,
      roomTypeOptions: nextIds.reduce(
        (options, id) => ({
          ...options,
          [id]: form.roomTypeOptions?.[id] ?? "0",
        }),
        {},
      ),
    };
    setSelectedRooms(nextRooms);
    setForm(nextForm);
    updateData?.(nextForm);
  };

  // Prune selected rooms if their pet type gets deselected
  useEffect(() => {
    if (
      loadingPetTypes ||
      loadingRooms ||
      !isPetTypeInitialized.current ||
      selectedPetTypes.length === 0 ||
      filteredRooms.length === 0
    ) {
      return;
    }

    setSelectedRooms((prev) => {
      const nextRooms = prev.filter((room) =>
        filteredRooms.some(
          (r) => String(r._id || r.id) === String(room._id || room.id),
        ),
      );
      const prevIds = prev.map((room) => room._id).join(",");
      const nextIds = nextRooms.map((room) => room._id).join(",");
      return prevIds === nextIds ? prev : nextRooms;
    });
  }, [
    filteredRooms,
    loadingPetTypes,
    loadingRooms,
    selectedPetTypes.length,
  ]);

  // Push selectedRooms into form → drives price defaults + gets saved
  // useEffect(() => {
  //   const roomIds = selectedRooms.map((r) => r._id);
  //   setForm((prev) => {
  //     const options = {};
  //     roomIds.forEach((id) => {
  //       options[id] = prev.roomTypeOptions?.[id] ?? "0";
  //     });
  //     return {
  //       ...prev,
  //       selectedRoomTypes: roomIds,
  //       roomTypeOptions: options,
  //     };
  //   });
  //   validate("rooms");
  //   setTouched((prev) => ({ ...prev, rooms: true }));
  // }, [selectedRooms]);

  // Pre-fill selectedRooms when editing an existing service
  useEffect(() => {
    if (isActive && rooms.length > 0 && data.selectedRoomTypes?.length > 0) {
      const selectedRoomIds = new Set(
        data.selectedRoomTypes.map((room) =>
          String(room?._id || room?.id || room?.room_type_id || room),
        ),
      );
      const preSelected = rooms.filter((r) =>
        selectedRoomIds.has(String(r._id || r.id)),
      );
      setSelectedRooms((prev) => {
        const prevIds = prev
          .map((room) => String(room._id || room.id))
          .join(",");
        const nextIds = preSelected
          .map((room) => String(room._id || room.id))
          .join(",");
        return prevIds === nextIds ? prev : preSelected;
      });
    }
  }, [isActive, rooms, data.selectedRoomTypes]);
  // Pet Type Handlers
  const togglePetType = (pt) => {
    setSelectedPetTypes((prev) => {
      const id = pt.pet_type_id || pt._id;
      const exists = prev.some((p) => (p.pet_type_id || p._id) === id);

      if (exists) {
        return prev.filter((p) => (p.pet_type_id || p._id) !== id);
      }

      return [...prev, pt]; // ✅ always array
    });
  };

  const removePetType = (id) => {
    setSelectedPetTypes((prev) =>
      prev.filter((p) => (p.pet_type_id || p._id) !== id),
    );
    validate("pet_type");
  };

  const toggleSelectAll = () => {
    setSelectedPetTypes(selectAll ? [] : [...petTypes]);
  };

  // Debounced check for duplicate service name
  const checkServiceNameExistsOnServer = useCallback(
    debounce(async (serviceName) => {
      if (!serviceName || serviceName.length < 3) {
        setServiceNameExists(false);
        return;
      }

      setCheckingServiceName(true);
      try {
        const response = await httpService.get(
          "vendorroomservice/getRoomServices",
        );

        if (response.data && Array.isArray(response.data.data)) {
          const services = response.data.data;
          const nameExists = services.some(
            (service) =>
              service.name.toLowerCase() === serviceName.toLowerCase() &&
              (!propRoomId || service._id !== propRoomId),
          );

          setServiceNameExists(nameExists);
        }
      } catch (error) {
        console.error("Error checking service name:", error);
      } finally {
        setCheckingServiceName(false);
      }
    }, 500),
    [propRoomId],
  );

  useEffect(() => {
    if (serviceNameExists) {
      toast.error(`Service name "${form.name}" already exists`);
    }
  }, [serviceNameExists, form.name]);

  const validate = useCallback(
    (field, value) => {
      const newErrors = { ...errors };

      switch (field) {
        case "name":
          if (!value?.trim()) newErrors.name = "Service Name is required";
          else if (value.length < 3)
            newErrors.name = "Name must be at least 3 characters";
          else if (serviceNameExists)
            newErrors.name = "Service name already exists";
          else delete newErrors.name;
          break;

          // Inside RoomForm.js validation logic
case "rooms": {
  if (selectedRooms.length === 0) {
    newErrors.rooms = "Please select at least one room";
  } else {
    // Check if every selected pet type is covered by at least one selected room
    const missingPetTypes = selectedPetTypes.filter((pt) => {
      const ptId = String(pt.pet_type_id || pt._id);

      // Check if any selected room supports this pet type ID[cite: 1]
      const hasMatchingRoom = selectedRooms.some((room) =>
        (room.pet_type || []).some(
          (roomPt) =>
            String(roomPt.id || roomPt._id || roomPt.pet_type_id) === ptId,
        ),
      );

      return !hasMatchingRoom;
    });

    if (missingPetTypes.length > 0) {
      const names = missingPetTypes.map((p) => p.name || "selected types").join(" and ");
      newErrors.rooms = `Please select at least one room that supports ${names}`;
    } else {
      delete newErrors.rooms;
    }
  }
  break;
}

        // case "category":
        //   if (value?.service_category_type_id === "")
        //     newErrors.category = "Service Category is required";
        //   else delete newErrors.category;
        //   break;
        case "basicPrice":
          if (value === "") newErrors.basicPrice = "Basic Price is required";
          else if (Number(value) === 0)
            newErrors.basicPrice = "Basic Price cannot be zero";
          else delete newErrors.basicPrice;
          break;
        case "pet_type":
          if (selectedPetTypes.length === 0)
            newErrors.pet_type = "At least one Pet Type is required";
          else delete newErrors.pet_type;
          break;
        case "taxPercentage":
          if (form.taxable === "yes" && (!value || Number(value) <= 0))
            newErrors.taxPercentage =
              "Tax Percentage is required when taxable is yes";
          else delete newErrors.taxPercentage;
          break;

        case "roomTypeSelections": {
          const missing = selectedPetTypes.filter((pt) => {
            const id = pt.pet_type_id || pt._id;
            return !(form.roomTypeSelections?.[id]?.length > 0);
          });
          if (missing.length > 0) {
            newErrors.roomTypeSelections = `Select at least 1 room for ${missing
              .map((p) => p.name)
              .join(", ")}`;
          } else {
            delete newErrors.roomTypeSelections;
          }
          break;
        }
        default:
          break;
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [errors, form.taxable, serviceNameExists, selectedPetTypes.length],
  );

  const handleChange = useCallback(
    (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      validate(field, value);

      if (field === "name") {
        checkServiceNameExistsOnServer(value);
      }
    },
    [validate, checkServiceNameExistsOnServer],
  );

  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      validate(field, form[field]);
    },
    [validate, form],
  );

  // Push selectedRooms into form → drives price defaults + gets saved
  useLayoutEffect(() => {
    if (
      propRoomId &&
      selectedRooms.length === 0 &&
      data.selectedRoomTypes?.length > 0
    ) {
      return;
    }

    const roomIds = selectedRooms.map((room) => room._id || room.id);
    const currentRoomIds = Array.isArray(form.selectedRoomTypes)
      ? form.selectedRoomTypes
      : [];
    const currentOptions = form.roomTypeOptions || {};
    const options = {};
    roomIds.forEach((id) => {
      options[id] = currentOptions[id] ?? "0";
    });
    const sameRoomIds =
      currentRoomIds.length === roomIds.length &&
      roomIds.every((id, index) => String(currentRoomIds[index]) === String(id));
    const sameOptions =
      sameRoomIds &&
      Object.keys(currentOptions).length === Object.keys(options).length &&
      roomIds.every(
        (id) => String(currentOptions[id] ?? "0") === String(options[id] ?? "0"),
      );

    if (sameRoomIds && sameOptions) {
      return;
    }

    const nextForm = {
      ...form,
      selectedRoomTypes: roomIds,
      roomTypeOptions: options,
    };
    setForm(nextForm);
    updateData?.(nextForm);
    validate("rooms");
    setTouched((prev) => ({ ...prev, rooms: true }));
  }, [selectedRooms, form, updateData, validate]);

  const debouncedValidationChange = useMemo(
    () =>
      debounce((isValid) => {
        onValidationChange?.(isValid);
      }, 300),
    [onValidationChange],
  );

  useEffect(() => {
    const isValid =
      !checkingServiceName &&
      form.name?.trim() &&
      form.name.length >= 3 &&
      !serviceNameExists &&
      // form.category?.service_category_type_id !== "" &&
      selectedPetTypes.length > 0 &&
      selectedRooms.length > 0 &&
      Object.keys(errors).length === 0;

    debouncedValidationChange(isValid);
  }, [
    form,
    serviceNameExists,
    checkingServiceName,
    errors,
    selectedPetTypes.length,
    selectedRooms.length,
    debouncedValidationChange,
  ]);

  const handleDefaultTemplate = useCallback(() => {
    const defaultPetType =
      petTypes.find((pt) => pt.name === "Dog") || petTypes[0];

    const template = {
      ...form,
      pet_type: defaultPetType
        ? [defaultPetType.pet_type_id || defaultPetType._id]
        : [],
      basicPrice: 1000,
      taxable: "yes",
      taxPercentage: "5",
    };

    setForm(template);
    setSelectedPetTypes(defaultPetType ? [defaultPetType] : []);
    updateData(template);
    toast.success("Default template applied successfully");
  }, [updateData, form, petTypes]);

  const debouncedUpdateData = useMemo(
    () =>
      debounce((formData) => {
        updateData(formData);
      }, 300),
    [updateData],
  );

  useEffect(() => {
  if (!isActive) {
    debouncedUpdateData.flush();
  }
}, [isActive, debouncedUpdateData]);

  useEffect(() => {
    debouncedUpdateData(form);
    return () => {
      debouncedUpdateData.cancel();
      checkServiceNameExistsOnServer.cancel();
    };
  }, [form, debouncedUpdateData, checkServiceNameExistsOnServer]);

  // const onlyType2Option = useMemo(() => {
  //   let found = null;
  //   let count = 0;
  //   for (const cat of categories) {
  //     if (cat.type_id !== 2) continue;
  //     for (const st of cat.service_type) {
  //       count += 1;
  //       if (count > 1) return null;
  //       found = {
  //         service_category_type_id: st.service_category_type_id,
  //         service_type_id: st.service_type_id,
  //         service_type_name: st.service_type_name,
  //         service_category_id: cat.service_category_id,
  //         type_id: cat.type_id,
  //         service_category_name: cat.service_category_name,
  //       };
  //     }
  //   }
  //   return count === 1 ? found : null;
  // }, [categories]);

  // const handleCategoryChange = useCallback(
  //   (value, e) => {
  //     const dataSet = e.target.selectedOptions[0].dataset;
  //     const serviceData = {
  //       service_category_id: dataSet.servicecategoryid,
  //       service_type_id: dataSet.servictypeid,
  //       service_type_name: dataSet.subcatname,
  //       service_category_type_id: value,
  //       type_id: dataSet.typeid,
  //       service_category_name: dataSet.catname,
  //     };
  //     setServiceCategory(serviceData);
  //     setForm((prev) => ({ ...prev, category: serviceData }));
  //     setTouched((prev) => ({ ...prev, category: true }));
  //   },
  //   [setServiceCategory]
  // );

  // useEffect(() => {
  //   if (onlyType2Option) {
  //     const { service_category_type_id: value, ...rest } = onlyType2Option;
  //     const fakeEvent = {
  //       target: {
  //         selectedOptions: [
  //           {
  //             dataset: {
  //               servicecategoryid: rest.service_category_id,
  //               servictypeid: rest.service_type_id,
  //               subcatname: rest.service_type_name,
  //               typeid: rest.type_id,
  //               catname: rest.service_category_name,
  //             },
  //           },
  //         ],
  //       },
  //     };
  //     handleCategoryChange(value, fakeEvent);
  //   }
  // }, [onlyType2Option, handleCategoryChange]);

  // Auto-close Pet Type dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (event) => {
      // Target the FormField wrapper with z-[60]
      const petTypeWrapper = document.querySelector(".relative.z-\\[60\\]");
      if (petTypeWrapper && !petTypeWrapper.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    // Use mousedown instead of click for better UX (closes faster)
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-medium">Basic Information</h3>
        <button
          type="button"
          onClick={handleDefaultTemplate}
          disabled={checkingServiceName || loadingPetTypes}
          className="items-center p-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Use Default Template
        </button>
      </div>

      {/* {categories[0]?.service_type.length > 1 && (
        <div className="flex gap-6 items-end">
          <FormField
            label="Service Category"
            required
            error={touched.category && errors.category}
            classApplied="basis-[100%]"
          >
            <ServiceCategorySelector
              selected={form.category || []}
              onChange={(newSelected) => {
                setForm((prev) => ({
                  ...prev,
                  category: newSelected[0],
                }));
              }}
              isMultiple={false}
              gridCols="grid-cols-2"
              includePetStore={false}
              includeOnlyBoarding={true}
            />
          </FormField>
        </div>
      )} */}

      <div className="w-full mb-4">
        <FormField
          label="Service Name"
          required
          error={touched.name && errors.name}
        >
          <div className="relative">
            <input
              type="text"
              value={form.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              onBlur={() => handleBlur("name")}
              className={`block w-full p-2 border rounded ${
                touched.name && errors.name
                  ? "border-red-300"
                  : "border-gray-300"
              }`}
              placeholder="Enter service name"
            />
            {checkingServiceName && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              </div>
            )}
            {!checkingServiceName && serviceNameExists && (
              <div className="absolute right-3 top-3 text-red-500">
                <FiXCircle className="w-4 h-4" />
              </div>
            )}
            {!checkingServiceName &&
              !serviceNameExists &&
              form.name &&
              form.name.length >= 3 && (
                <div className="absolute right-3 top-3 text-green-500">
                  <FiCheck className="w-4 h-4" />
                </div>
              )}
          </div>
        </FormField>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        {/* Multi-Select Pet Type */}
        <FormField
          label="Pet Type"
          required
          error={touched.pet_type && errors.pet_type}
          classApplied="relative z-[60]" // Important: keeps dropdown above other elements
        >
          <div className="relative" ref={dropdownRef}>
            {/* Trigger Box */}
            <div
              className="w-full px-3 py-2 border rounded bg-white min-h-[42px] flex flex-wrap items-center gap-2 cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              {selectedPetTypes.length === 0 ? (
                <span className="text-gray-500">
                  {loadingPetTypes
                    ? "Loading pet types..."
                    : "Select Pet Types"}
                </span>
              ) : (
                selectedPetTypes.map((pt) => (
                  <span
                    key={pt.pet_type_id || pt._id}
                    className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full"
                  >
                    {pt.name}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removePetType(pt.pet_type_id || pt._id);
                      }}
                      className="ml-1 hover:bg-green-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold"
                    >
                      ×
                    </button>
                  </span>
                ))
              )}

              <svg
                className={`ml-auto w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[70] max-h-60 overflow-y-auto" // z-[70] ensures it's above everything
                onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside
              >
                {/* Select All */}
                <div
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelectAll();
                  }}
                >
                  <div className="w-5 h-5 rounded border-2 border-gray-400 flex items-center justify-center">
                    {selectAll && (
                      <span className="text-green-600 font-bold">✓</span>
                    )}
                  </div>
                  <span className="font-medium">
                    {selectAll ? "Unselect All" : "Select All"}
                  </span>
                </div>

                {/* Loading / Empty / List */}
                {loadingPetTypes ? (
                  <div className="px-4 py-3 text-gray-500">Loading...</div>
                ) : petTypes.length === 0 ? (
                  <div className="px-4 py-3 text-gray-500">
                    No pet types available
                  </div>
                ) : (
                  petTypes.map((pt) => {
                    const isSelected = selectedPetTypes.some(
                      (s) =>
                        (s.pet_type_id || s._id) === (pt.pet_type_id || pt._id),
                    );

                    return (
                      <div
                        key={pt.pet_type_id || pt._id}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePetType(pt);
                        }}
                      >
                        <div className="w-5 h-5 rounded border-2 border-gray-400 flex items-center justify-center">
                          {isSelected && (
                            <span className="text-green-600 font-bold">✓</span>
                          )}
                        </div>
                        <span>{pt.name}</span>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </FormField>

        <FormField label="Bookable Online">
          <select
            value={form.bookable_online || "yes"}
            onChange={(e) => handleChange("bookable_online", e.target.value)}
            className="block w-full p-2 border border-gray-300 rounded"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-6 mb-4">
        <FormField
          label="Rooms"
          required
          error={touched.rooms && errors.rooms}
          classApplied="relative z-[50]"
        >
          <div className="relative" ref={roomsDropdownRef}>
            <div
              className="w-full border border-gray-300 rounded-md bg-white min-h-[42px] flex items-stretch overflow-hidden focus-within:ring-1 focus-within:ring-blue-500"
            >
              <div
                className="flex-1 px-3 py-2 flex flex-wrap items-center gap-2 cursor-pointer"
                onClick={() => setRoomsDropdownOpen((prev) => !prev)}
              >
              {selectedRooms.length === 0 ? (
                <span className="text-gray-500">
                  {loadingRooms ? "Loading rooms..." : "Select Rooms"}
                </span>
              ) : (
                selectedRooms.map((room) => (
                  <span
                    key={room._id}
                    className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full"
                  >
                    {room.room_name} (
                    {room.pet_type.map((pt) => pt.name).join(", ")})
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRoom(room._id);
                      }}
                      className="ml-1 hover:bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold"
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
                <svg
                  className={`ml-auto w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    roomsDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              {onAddRoom && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddRoom();
                  }}
                  className="shrink-0 border-l border-gray-200 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                  + Add Room
                </button>
              )}
            </div>

            {roomsDropdownOpen && (
  <div
    className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[60] max-h-60 overflow-y-auto"
    onClick={(e) => e.stopPropagation()}
  >
    {loadingRooms ? (
      <div className="px-4 py-3 text-gray-500">Loading...</div>
    ) : filteredRooms.length === 0 ? (
      <div className="px-4 py-3 text-gray-500">
        {selectedPetTypes.length === 0
          ? "Please select pet types first"
          : "No rooms available for selected pet types"}
      </div>
    ) : (
      <>
        {filteredRooms.map((room) => {
          // ... existing room item rendering (unchanged)
          const isSelected = selectedRooms.some(
            (selectedRoom) =>
              String(selectedRoom._id || selectedRoom.id) ===
              String(room._id || room.id),
          );
          const petTypeNames = room.pet_type.map((pt) => pt.name).join(", ");
          return (
            <div
              key={room._id}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
              onClick={(e) => {
                e.stopPropagation();
                toggleRoom(room);
              }}
            >
              <div className="w-5 h-5 rounded border-2 border-gray-400 flex items-center justify-center">
                {isSelected && <span className="text-green-600 font-bold">✓</span>}
              </div>
              <span>
                {room.room_name}{" "}
                <span className="text-xs text-gray-500">({petTypeNames})</span>
              </span>
            </div>
          );
        })}
      </>
    )}
  </div>
)}
          </div>
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-6 mt-2">
        <FormField
          label="Basic Price"
          required
          error={touched.basicPrice && errors.basicPrice}
        >
          <input
            min={0}
            type="number"
            placeholder="Enter Basic Price"
            className={`block w-full p-2 border rounded ${
              touched.basicPrice && errors.basicPrice
                ? "border-red-300"
                : "border-gray-300"
            }`}
            value={form.basicPrice || ""}
            onBlur={() => handleBlur("basicPrice")}
            onChange={(e) => handleChange("basicPrice", e.target.value)}
          />
        </FormField>
      </div>

      <div className="mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1">
              Do you want to add separate service charges?
            </label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="yes"
                  checked={form.taxable === "yes"}
                  onChange={(e) => handleChange("taxable", e.target.value)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="ml-2">Yes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="no"
                  checked={form.taxable === "no"}
                  onChange={(e) => handleChange("taxable", e.target.value)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="ml-2">No</span>
              </label>
            </div>
          </div>

          {form.taxable === "yes" && (
            <div>
              <FormField
                label="Service Charges (%)"
                required={form.taxable === "yes"}
                error={touched.taxPercentage && errors.taxPercentage}
              >
                <input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Enter Service Charges % (e.g., 5 or 18)"
                  className={`block w-full p-2 border rounded ${
                    touched.taxPercentage && errors.taxPercentage
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  value={form.taxPercentage || ""}
                  onChange={(e) =>
                    handleChange("taxPercentage", e.target.value)
                  }
                  onBlur={() => handleBlur("taxPercentage")}
                />
              </FormField>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium mb-3">Additional Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              value={form.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              className="p-2 w-full min-h-[120px] border border-gray-300 rounded"
              placeholder="Enter a detailed description of the room add-ons..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Images</label>
            <FileUpload
              uploadEndpoint="/upload/fileUpload"
              acceptedTypes={["image/png", "image/jpeg", "image/gif"]}
              multiple={true}
              maxFiles={10}
              moduleName="vendors"
              docType="boardingService"
              existingUrls={form.images || []}
              onUploadComplete={(urls) => {
                setForm((prev) => ({
                  ...prev,
                  images: urls,
                }));
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default React.memo(RoomForm);
